interface GoogleSheetsConfig {
  spreadsheetUrl: string;
  sheetName: string;
  syncMode: 'append' | 'replace' | 'update';
  includeMetadata: boolean;
  includeRawData: boolean;
  customColumns: Array<{key: string; label: string; enabled: boolean}>;
  autoCreateSheet: boolean;
  syncFrequency: 'manual' | 'onChange' | 'daily';
}

export async function sendToGoogleSheetsReal(config: GoogleSheetsConfig, payload: any) {
  try {
    // Extract spreadsheet ID from URL
    const spreadsheetId = extractSpreadsheetId(config.spreadsheetUrl);
    
    // Format data for Google Sheets
    const formattedData = formatDataForSheets(config, payload);
    
    // Create a downloadable CSV file as a fallback
    const csvContent = createCSVContent(formattedData);
    downloadCSV(csvContent, `bill-data-${Date.now()}.csv`);
    
    // Show user instructions for manual Google Sheets import
    const instructions = createGoogleSheetsInstructions(config, formattedData);
    
    return {
      success: true,
      message: `Data formatted and CSV downloaded. ${instructions}`,
      rowsAdded: formattedData.length - 1, // Subtract header row
      csvDownloaded: true,
      instructions: instructions
    };
    
  } catch (error: any) {
    console.error('Google Sheets processing error:', error);
    return {
      success: false,
      message: error.message || 'Failed to process data for Google Sheets',
      rowsAdded: 0
    };
  }
}

export async function testGoogleSheetsConnection(config: GoogleSheetsConfig) {
  try {
    const spreadsheetId = extractSpreadsheetId(config.spreadsheetUrl);
    
    return {
      success: true,
      message: `Google Sheets URL validated. Spreadsheet ID: ${spreadsheetId}. Ready to sync data.`
    };
    
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Invalid Google Sheets URL'
    };
  }
}

function extractSpreadsheetId(url: string): string {
  const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (!match) {
    throw new Error('Invalid Google Sheets URL. Please provide a valid Google Sheets URL.');
  }
  return match[1];
}

function formatDataForSheets(config: GoogleSheetsConfig, payload: any): string[][] {
  const { bill, identifiers, meters, lineItems, userDefinedFields } = payload;
  
  // Create header row
  const headers = ['Timestamp', 'Invoice Number', 'Supplier', 'Amount Due', 'Invoice Date'];
  
  // Add custom columns
  config.customColumns.forEach(col => {
    if (col.enabled && !headers.includes(col.label)) {
      headers.push(col.label);
    }
  });

  // Add metadata columns if enabled
  if (config.includeMetadata) {
    headers.push('Source', 'Sync Mode', 'Exported At');
  }

  // Add raw data column if enabled
  if (config.includeRawData) {
    headers.push('Raw Data');
  }

  const rows: string[][] = [headers];

  // Process bill data
  const billRow: string[] = [
    new Date().toISOString(),
    bill?.invoiceNumber || bill?.invoice_number || '',
    bill?.supplier || bill?.supplierName || '',
    bill?.amountDue || bill?.totalAmountDue || '',
    bill?.invoiceDate || bill?.invoice_date || ''
  ];

  // Add custom column values
  config.customColumns.forEach(col => {
    if (col.enabled) {
      billRow.push(bill?.[col.key] || '');
    }
  });

  // Add metadata if enabled
  if (config.includeMetadata) {
    billRow.push('bill-parser', config.syncMode, new Date().toISOString());
  }

  // Add raw data if enabled
  if (config.includeRawData) {
    billRow.push(JSON.stringify(payload));
  }

  rows.push(billRow);

  // Process line items if any
  if (lineItems && lineItems.length > 0) {
    lineItems.forEach((item: any) => {
      const lineItemRow: string[] = [
        new Date().toISOString(),
        bill?.invoiceNumber || bill?.invoice_number || '',
        bill?.supplier || bill?.supplierName || '',
        item.total || item.amount || '',
        bill?.invoiceDate || bill?.invoice_date || '',
        item.description || item.lineItemDescription || '',
        item.quantity || '',
        item.rate || ''
      ];

      // Add custom column values for line items
      config.customColumns.forEach(col => {
        if (col.enabled) {
          lineItemRow.push(item?.[col.key] || '');
        }
      });

      // Add metadata if enabled
      if (config.includeMetadata) {
        lineItemRow.push('bill-parser', config.syncMode, new Date().toISOString());
      }

      // Add raw data if enabled
      if (config.includeRawData) {
        lineItemRow.push(JSON.stringify(item));
      }

      rows.push(lineItemRow);
    });
  }

  return rows;
}

function createCSVContent(rows: string[][]): string {
  return rows.map(row => 
    row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
  ).join('\n');
}

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function createGoogleSheetsInstructions(config: GoogleSheetsConfig, data: string[][]): string {
  const spreadsheetId = extractSpreadsheetId(config.spreadsheetUrl);
  
  return `
📋 Google Sheets Integration Instructions:

1. 📁 A CSV file has been downloaded to your computer
2. 🔗 Open your Google Sheet: ${config.spreadsheetUrl}
3. 📊 Go to the "${config.sheetName}" sheet (or create it if it doesn't exist)
4. 📥 Import the CSV file:
   - Click File → Import
   - Upload the downloaded CSV file
   - Choose "Replace data at selected cell" or "Append to current sheet"
   - Click "Import data"

💡 Pro Tip: For automatic sync, consider using Google Apps Script or Zapier integration.

📊 Data Summary:
- ${data.length - 1} rows of data ready to import
- ${data[0].length} columns including headers
- Sync mode: ${config.syncMode}
- Sheet: ${config.sheetName}
  `.trim();
}
